import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
    FaStar, FaEdit, FaTrash, FaPlus, FaSave, FaTimes, FaUser, FaCog, 
    FaRobot, FaBrain, FaSearch, FaFilter, FaDownload, FaUpload 
} from 'react-icons/fa';

const AdminPromptManager = () => {
    const { user } = useAuth();
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [prompts, setPrompts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [llmModels, setLlmModels] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // États pour les filtres
    const [serviceFilter, setServiceFilter] = useState('all');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    
    // États pour la création/édition
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [editingPrompt, setEditingPrompt] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        content: '',
        category_id: '',
        service_type: 'analysis',
        is_default: false,
        llm_model: 'groq-mixtral'
    });
    
    // États pour la gestion des modèles LLM
    const [showLlmConfig, setShowLlmConfig] = useState(false);
    const [userLlmConfig, setUserLlmConfig] = useState({
        default_model: 'groq-mixtral',
        temperature: 0.7,
        max_tokens: 4000,
        custom_settings: {}
    });

    // Vérifier si l'utilisateur est admin
    const isAdmin = user?.role === 'admin';

    useEffect(() => {
        if (user && isAdmin) {
            fetchUsers();
            fetchCategories();
            fetchLlmModels();
        }
    }, [user, isAdmin]);

    useEffect(() => {
        if (selectedUser) {
            fetchUserPrompts();
            fetchUserLlmConfig();
        }
    }, [selectedUser, serviceFilter, categoryFilter]);

    const fetchUsers = async () => {
        try {
            const response = await fetch('/api/auth/users', {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });
            if (response.ok) {
                const data = await response.json();
                setUsers(data);
            }
        } catch (error) {
            console.error('Erreur lors du chargement des utilisateurs:', error);
        }
    };

    const fetchUserPrompts = async () => {
        if (!selectedUser) return;
        
        try {
            let url = `/api/prompts/admin/user/${selectedUser.id}`;
            const params = new URLSearchParams();
            
            if (serviceFilter !== 'all') params.append('service_type', serviceFilter);
            if (categoryFilter !== 'all') params.append('category_id', categoryFilter);
            if (searchTerm) params.append('search', searchTerm);
            
            if (params.toString()) url += `?${params.toString()}`;
            
            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });
            
            if (response.ok) {
                const data = await response.json();
                setPrompts(data);
            } else {
                setError('Erreur lors du chargement des prompts');
            }
        } catch (error) {
            setError('Erreur de connexion');
        } finally {
            setLoading(false);
        }
    };

    const fetchCategories = async () => {
        try {
            const response = await fetch('/api/prompts/categories');
            if (response.ok) {
                const data = await response.json();
                setCategories(data);
            }
        } catch (error) {
            console.error('Erreur lors du chargement des catégories:', error);
        }
    };

    const fetchLlmModels = async () => {
        try {
            const response = await fetch('/api/llm/models', {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });
            if (response.ok) {
                const data = await response.json();
                setLlmModels(data);
            }
        } catch (error) {
            console.error('Erreur lors du chargement des modèles LLM:', error);
            // Modèles par défaut en cas d'erreur
            setLlmModels([
                { id: 'groq-mixtral', name: 'Groq Mixtral 8x7B', provider: 'groq' },
                { id: 'groq-llama', name: 'Groq Llama 3.1 70B', provider: 'groq' },
                { id: 'openai-gpt4', name: 'OpenAI GPT-4', provider: 'openai' },
                { id: 'claude-3', name: 'Anthropic Claude 3', provider: 'anthropic' }
            ]);
        }
    };

    const fetchUserLlmConfig = async () => {
        if (!selectedUser) return;
        
        try {
            const response = await fetch(`/api/llm/config/user/${selectedUser.id}`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });
            if (response.ok) {
                const data = await response.json();
                setUserLlmConfig(data);
            }
        } catch (error) {
            console.error('Erreur lors du chargement de la config LLM:', error);
        }
    };

    const handleCreatePrompt = async (e) => {
        e.preventDefault();
        try {
            const response = await fetch('/api/prompts/admin', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({
                    ...formData,
                    user_id: selectedUser.id
                }),
            });

            if (response.ok) {
                setShowCreateForm(false);
                setFormData({ 
                    name: '', description: '', content: '', category_id: '', 
                    service_type: 'analysis', is_default: false, llm_model: 'groq-mixtral' 
                });
                fetchUserPrompts();
            } else {
                setError('Erreur lors de la création du prompt');
            }
        } catch (error) {
            setError('Erreur de connexion');
        }
    };

    const handleUpdatePrompt = async (e) => {
        e.preventDefault();
        try {
            const response = await fetch(`/api/prompts/admin/${editingPrompt.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify(formData),
            });

            if (response.ok) {
                setEditingPrompt(null);
                setFormData({ 
                    name: '', description: '', content: '', category_id: '', 
                    service_type: 'analysis', is_default: false, llm_model: 'groq-mixtral' 
                });
                fetchUserPrompts();
            } else {
                setError('Erreur lors de la mise à jour du prompt');
            }
        } catch (error) {
            setError('Erreur de connexion');
        }
    };

    const handleDeletePrompt = async (promptId) => {
        if (window.confirm('Êtes-vous sûr de vouloir supprimer ce prompt ?')) {
            try {
                const response = await fetch(`/api/prompts/admin/${promptId}`, {
                    method: 'DELETE',
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                });

                if (response.ok) {
                    fetchUserPrompts();
                } else {
                    setError('Erreur lors de la suppression du prompt');
                }
            } catch (error) {
                setError('Erreur de connexion');
            }
        }
    };

    const handleUpdateLlmConfig = async () => {
        try {
            const response = await fetch(`/api/llm/config/user/${selectedUser.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify(userLlmConfig),
            });

            if (response.ok) {
                setShowLlmConfig(false);
                setError(null);
            } else {
                setError('Erreur lors de la mise à jour de la configuration LLM');
            }
        } catch (error) {
            setError('Erreur de connexion');
        }
    };

    const exportUserPrompts = async () => {
        if (!selectedUser) return;
        
        try {
            const response = await fetch(`/api/prompts/admin/export/${selectedUser.id}`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });
            
            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `prompts_${selectedUser.email}_${new Date().toISOString().split('T')[0]}.json`;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
            }
        } catch (error) {
            setError('Erreur lors de l\'export');
        }
    };

    const startEdit = (prompt) => {
        setEditingPrompt(prompt);
        setFormData({
            name: prompt.name,
            description: prompt.description,
            content: prompt.content,
            category_id: prompt.category_id || '',
            service_type: prompt.service_type || 'analysis',
            is_default: prompt.is_default || false,
            llm_model: prompt.llm_model || 'groq-mixtral'
        });
    };

    if (!isAdmin) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <FaTimes className="mx-auto h-12 w-12 text-red-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">Accès refusé</h3>
                    <p className="mt-1 text-sm text-gray-500">
                        Vous devez être administrateur pour accéder à cette page.
                    </p>
                </div>
            </div>
        );
    }

    if (loading && !selectedUser) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Chargement...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">
                        <FaCog className="inline mr-3" />
                        Administration des Prompts & Modèles LLM
                    </h1>
                    <p className="text-gray-600">
                        Gérez les prompts et modèles LLM pour chaque utilisateur
                    </p>
                </div>

                {error && (
                    <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
                        <p className="text-red-800">{error}</p>
                    </div>
                )}

                {/* Sélection utilisateur */}
                <div className="mb-6 bg-white rounded-lg shadow p-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                        <FaUser className="inline mr-2" />
                        Sélection de l'utilisateur
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Utilisateur à gérer
                            </label>
                            <select
                                value={selectedUser?.id || ''}
                                onChange={(e) => {
                                    const userId = e.target.value;
                                    const user = users.find(u => u.id === parseInt(userId));
                                    setSelectedUser(user || null);
                                }}
                                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                            >
                                <option value="">Sélectionner un utilisateur</option>
                                {users.map(u => (
                                    <option key={u.id} value={u.id}>
                                        {u.email} ({u.full_name}) - {u.role}
                                    </option>
                                ))}
                            </select>
                        </div>
                        
                        {selectedUser && (
                            <div className="flex items-end space-x-2">
                                <button
                                    onClick={() => setShowLlmConfig(true)}
                                    className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 flex items-center"
                                >
                                    <FaBrain className="mr-2" />
                                    Config LLM
                                </button>
                                <button
                                    onClick={exportUserPrompts}
                                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center"
                                >
                                    <FaDownload className="mr-2" />
                                    Export
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {selectedUser && (
                    <>
                        {/* Filtres et recherche */}
                        <div className="mb-6 bg-white rounded-lg shadow p-6">
                            <h3 className="text-lg font-medium text-gray-900 mb-4">
                                <FaFilter className="inline mr-2" />
                                Filtres et recherche
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Service
                                    </label>
                                    <select
                                        value={serviceFilter}
                                        onChange={(e) => setServiceFilter(e.target.value)}
                                        className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                                    >
                                        <option value="all">Tous les services</option>
                                        <option value="analysis">Service Analysis</option>
                                        <option value="llm">Service LLM</option>
                                    </select>
                                </div>
                                
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Catégorie
                                    </label>
                                    <select
                                        value={categoryFilter}
                                        onChange={(e) => setCategoryFilter(e.target.value)}
                                        className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                                    >
                                        <option value="all">Toutes les catégories</option>
                                        {categories.map(cat => (
                                            <option key={cat.id} value={cat.id}>
                                                {cat.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Recherche
                                    </label>
                                    <div className="relative">
                                        <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                        <input
                                            type="text"
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            placeholder="Rechercher..."
                                            className="w-full pl-10 border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>
                                </div>
                                
                                <div className="flex items-end">
                                    <button
                                        onClick={() => setShowCreateForm(true)}
                                        className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center justify-center"
                                    >
                                        <FaPlus className="mr-2" />
                                        Nouveau Prompt
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Informations utilisateur */}
                        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h4 className="text-lg font-medium text-blue-900">
                                        Gestion pour : {selectedUser.email}
                                    </h4>
                                    <p className="text-blue-700">
                                        {selectedUser.full_name} - Rôle: {selectedUser.role}
                                    </p>
                                </div>
                                <div className="text-right">
                                    <p className="text-sm text-blue-600">
                                        {prompts.length} prompt(s) configuré(s)
                                    </p>
                                    <p className="text-sm text-blue-600">
                                        Modèle LLM: {userLlmConfig.default_model}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Configuration LLM Modal */}
                        {showLlmConfig && (
                            <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                                <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
                                    <div className="mt-3">
                                        <h3 className="text-lg font-medium text-gray-900 mb-4">
                                            <FaBrain className="inline mr-2" />
                                            Configuration LLM pour {selectedUser.email}
                                        </h3>
                                        
                                        <div className="space-y-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Modèle par défaut
                                                </label>
                                                <select
                                                    value={userLlmConfig.default_model}
                                                    onChange={(e) => setUserLlmConfig({
                                                        ...userLlmConfig,
                                                        default_model: e.target.value
                                                    })}
                                                    className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                                                >
                                                    {llmModels.map(model => (
                                                        <option key={model.id} value={model.id}>
                                                            {model.name} ({model.provider})
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Température ({userLlmConfig.temperature})
                                                </label>
                                                <input
                                                    type="range"
                                                    min="0"
                                                    max="2"
                                                    step="0.1"
                                                    value={userLlmConfig.temperature}
                                                    onChange={(e) => setUserLlmConfig({
                                                        ...userLlmConfig,
                                                        temperature: parseFloat(e.target.value)
                                                    })}
                                                    className="w-full"
                                                />
                                                <div className="flex justify-between text-xs text-gray-500">
                                                    <span>Précis</span>
                                                    <span>Créatif</span>
                                                </div>
                                            </div>
                                            
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Tokens maximum
                                                </label>
                                                <input
                                                    type="number"
                                                    min="100"
                                                    max="8000"
                                                    value={userLlmConfig.max_tokens}
                                                    onChange={(e) => setUserLlmConfig({
                                                        ...userLlmConfig,
                                                        max_tokens: parseInt(e.target.value)
                                                    })}
                                                    className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                                                />
                                            </div>
                                        </div>
                                        
                                        <div className="mt-6 flex justify-end space-x-3">
                                            <button
                                                onClick={() => setShowLlmConfig(false)}
                                                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                                            >
                                                Annuler
                                            </button>
                                            <button
                                                onClick={handleUpdateLlmConfig}
                                                className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
                                            >
                                                Sauvegarder
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Formulaire de création/édition */}
                        {(showCreateForm || editingPrompt) && (
                            <div className="mb-8 bg-white rounded-lg shadow p-6">
                                <h3 className="text-lg font-medium text-gray-900 mb-4">
                                    {editingPrompt ? 'Modifier le prompt' : 'Créer un nouveau prompt'}
                                </h3>
                                <form onSubmit={editingPrompt ? handleUpdatePrompt : handleCreatePrompt}>
                                    <div className="grid grid-cols-1 gap-6">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700">Nom</label>
                                                <input
                                                    type="text"
                                                    value={formData.name}
                                                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                                                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                                                    required
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700">Service</label>
                                                <select
                                                    value={formData.service_type}
                                                    onChange={(e) => setFormData({...formData, service_type: e.target.value})}
                                                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                                                    required
                                                >
                                                    <option value="analysis">Service Analysis</option>
                                                    <option value="llm">Service LLM</option>
                                                </select>
                                            </div>
                                        </div>
                                        
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700">Catégorie</label>
                                                <select
                                                    value={formData.category_id}
                                                    onChange={(e) => setFormData({...formData, category_id: e.target.value})}
                                                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                                                    required
                                                >
                                                    <option value="">Sélectionner une catégorie</option>
                                                    {categories.map(category => (
                                                        <option key={category.id} value={category.id}>
                                                            {category.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700">Modèle LLM</label>
                                                <select
                                                    value={formData.llm_model}
                                                    onChange={(e) => setFormData({...formData, llm_model: e.target.value})}
                                                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                                                >
                                                    {llmModels.map(model => (
                                                        <option key={model.id} value={model.id}>
                                                            {model.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                        
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Description</label>
                                            <input
                                                type="text"
                                                value={formData.description}
                                                onChange={(e) => setFormData({...formData, description: e.target.value})}
                                                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                                                required
                                            />
                                        </div>
                                        
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Contenu du prompt</label>
                                            <textarea
                                                value={formData.content}
                                                onChange={(e) => setFormData({...formData, content: e.target.value})}
                                                rows={8}
                                                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                                                required
                                                placeholder="Entrez le contenu du prompt système..."
                                            />
                                        </div>
                                        
                                        <div className="flex items-center">
                                            <input
                                                type="checkbox"
                                                id="is_default"
                                                checked={formData.is_default}
                                                onChange={(e) => setFormData({...formData, is_default: e.target.checked})}
                                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                            />
                                            <label htmlFor="is_default" className="ml-2 block text-sm text-gray-900">
                                                Définir comme prompt par défaut pour ce type
                                            </label>
                                        </div>
                                    </div>
                                    
                                    <div className="mt-6 flex justify-end space-x-3">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setShowCreateForm(false);
                                                setEditingPrompt(null);
                                                setFormData({ 
                                                    name: '', description: '', content: '', category_id: '', 
                                                    service_type: 'analysis', is_default: false, llm_model: 'groq-mixtral' 
                                                });
                                            }}
                                            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                                        >
                                            <FaTimes className="mr-2" />
                                            Annuler
                                        </button>
                                        <button
                                            type="submit"
                                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
                                        >
                                            <FaSave className="mr-2" />
                                            {editingPrompt ? 'Sauvegarder' : 'Créer'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        )}

                        {/* Liste des prompts */}
                        <div className="grid grid-cols-1 gap-6">
                            {prompts.length === 0 ? (
                                <div className="text-center py-12 bg-white rounded-lg shadow">
                                    <FaRobot className="mx-auto h-12 w-12 text-gray-400" />
                                    <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun prompt trouvé</h3>
                                    <p className="mt-1 text-sm text-gray-500">
                                        {selectedUser ? 'Aucun prompt configuré pour cet utilisateur.' : 'Sélectionnez un utilisateur pour voir ses prompts.'}
                                    </p>
                                    {selectedUser && (
                                        <div className="mt-6">
                                            <button
                                                onClick={() => setShowCreateForm(true)}
                                                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                                            >
                                                <FaPlus className="mr-2" />
                                                Créer le premier prompt
                                            </button>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                prompts.map((prompt) => (
                                    <div key={prompt.id} className="bg-white rounded-lg shadow p-6">
                                        <div className="flex items-start justify-between">
                                            <div className="flex-1">
                                                <div className="flex items-center space-x-3 mb-2">
                                                    <h4 className="text-lg font-medium text-gray-900">
                                                        {prompt.name}
                                                    </h4>
                                                    {prompt.is_default && (
                                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                                            <FaStar className="mr-1" />
                                                            Par défaut
                                                        </span>
                                                    )}
                                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                                        prompt.service_type === 'analysis'
                                                            ? 'bg-blue-100 text-blue-800'
                                                            : 'bg-purple-100 text-purple-800'
                                                    }`}>
                                                        {prompt.service_type === 'analysis' ? 'Analysis' : 'LLM'}
                                                    </span>
                                                </div>
                                                
                                                <p className="text-gray-600 mb-3">{prompt.description}</p>
                                                
                                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-500 mb-4">
                                                    <div>
                                                        <span className="font-medium">Catégorie:</span> {prompt.category_name || 'Non définie'}
                                                    </div>
                                                    <div>
                                                        <span className="font-medium">Modèle LLM:</span> {prompt.llm_model || 'Par défaut'}
                                                    </div>
                                                    <div>
                                                        <span className="font-medium">Modifié:</span> {new Date(prompt.updated_at).toLocaleDateString()}
                                                    </div>
                                                </div>
                                                
                                                <div className="bg-gray-50 rounded-md p-3">
                                                    <p className="text-sm text-gray-700 font-mono">
                                                        {prompt.content.length > 200
                                                            ? `${prompt.content.substring(0, 200)}...`
                                                            : prompt.content
                                                        }
                                                    </p>
                                                </div>
                                            </div>
                                            
                                            <div className="flex items-center space-x-2 ml-4">
                                                <button
                                                    onClick={() => startEdit(prompt)}
                                                    className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md"
                                                    title="Modifier"
                                                >
                                                    <FaEdit />
                                                </button>
                                                <button
                                                    onClick={() => handleDeletePrompt(prompt.id)}
                                                    className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md"
                                                    title="Supprimer"
                                                >
                                                    <FaTrash />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default AdminPromptManager;